import { clamp } from '@/helpers/number'

/**
 * @function createStableImageBitmap
 * Safari and Edge polyfill for createImageBitmap which ended up being more stable than the real
 * implementation of createImageBitmap on Chrome.
 *
 * Context: using createImageBitmap on each frame used in the Cyclon video scrubbing was making
 * the Chrome tab crash on Android. Using this polyfill on Chrome as well solved the issue.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/createImageBitmap
 */
export function createStableImageBitmap(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const src = URL.createObjectURL(blob)
    img.src = src
    img.onload = () => {
      URL.revokeObjectURL(src)
      resolve(img)
    }
    img.onerror = reject
  })
}

/**
 * @function optimizeContentfulAssetUrls
 * Basic optimization of Contentful asset urls using their parameters. If you want to use the url
 * in an <img> tag, you should probably use ImageContentful.vue instead of this function.
 */
export function optimizeContentfulAssetUrls(
  assetUrls,
  { width, fixedWidth, quality = 71, format = 'jpg', devicePixelRatio = 1 }
) {
  if (!width && !fixedWidth) throw new Error('[optimizeContentfulAssetUrls] - "width" is required')

  const optimizedWidth = fixedWidth || Math.round(width * (1 + (devicePixelRatio - 1) / 2))
  const standardWidth = On.getContentfulFittingImageSize(optimizedWidth)

  const urlParams = [
    `w=${standardWidth}`, // has to be an integer
    `q=${quality}`,
    `fm=${format}`,
  ]
  return assetUrls.map(url => {
    // Add protocol (i.e. "https:") to make it an absolute url an avoid errors within Web workers
    const protocol = /^\/\//.test(url) ? self.location.protocol : ''
    return `${protocol}${url}?${urlParams.join('&')}`
  })
}

/**
 * Analyses all four sides of a given image and
 * checks if some portion of that side is not transparent.
 *
 * The higher the precision, the higher the number of pixels analysed and
 * the more time it will consume to compute.
 *
 * The tolerance controls when a pixel is considered transparent.
 * 0 (zero) = is transparent if 100% transparent
 * 0.5 = is transparent if up to 50% transparency
 *
 * @param {String} src: required
 * @param {Number} precision: 0.1 ... 1 (any other value is ignored) -> default: 0.2
 * @param {Number} tolerance: 0 ... 1 (any other value is ignored) -> default: 0.5
 *
 * @return {Object: {top: Boolean, right: Boolean, bottom: Boolean, left: Boolean}}
 * The returned object represents the 4 sides of the image:
 * {top, right, bottom, left}
 * It is TRUE if the side is transparent
 *
 * Example: ('img.jpg', .1)
 * - 0.1 means 10 pixels per side, so the canvas will render the picture with 10x10px.
 * - The analysier will scan 1 pixel from each side, so 40 pixes will be checked
 *
 *   →     ! !
 * ↓ 0 0 0 1 1 0 0 0 0 0 ↓
 *   0                 0
 *   0                 0
 *   0                 0
 *   0                 0
 *   0                 0
 *   0                 0
 *   0                 0
 *   0                 0
 *   0 0 0 0 0 0 0 0 0 0
 *   →
 *
 * This image result is: {top: false, right: true, bottom: true, left: true}
 * Since some value other than transparent was detected for 2 pixels in the top side.
 *
 * @see: https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Pixel_manipulation_with_canvas#a_color_picker
 * @see: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/getImageData
 * @see: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
 */
export function getSidesTransparency(src, precision = 0.2, tolerance = 0.5) {
  return new Promise((resolve, reject) => {
    if (!src) resolve({ top: false, right: false, bottom: false, left: false })

    const length = clamp(precision, 0.1, 1) * 100
    const transparency = clamp(tolerance, 0, 1) * 255
    const lastPixel = length - 1

    // canvas
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // img
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, length, length)

      // [0,1,2,3,...]
      const line = Array.from({ length }, (v, i) => i)

      // data[3] since channel 4 is the alpha channel: R,G,B,A
      const hasColor = (x, y) => ctx.getImageData(x, y, 1, 1).data[3] >= transparency

      resolve({
        top: !line.some(curPixel => hasColor(curPixel, 0)),
        right: !line.some(curPixel => hasColor(lastPixel, curPixel)),
        bottom: !line.some(curPixel => hasColor(curPixel, lastPixel)),
        left: !line.some(curPixel => hasColor(0, curPixel)),
      })
    }

    img.onerror = reject
    img.crossOrigin = 'anonymous'
    img.src = src
  })
}
